import React, { useEffect, useState } from "react";
import styles from "./OrderedTable.module.css";
import axios from "axios";
import { FiRefreshCw, FiSearch, FiTrash2, FiChevronDown, FiChevronUp } from "react-icons/fi";
import config from '../../config/config';

const OrderedTable = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchField, setSearchField] = useState("phone");
  const [expandedRows, setExpandedRows] = useState([]);

   const getOrderCounts = () => {
  const counts = {
    pending: { count: 0, amount: 0 },
    accepted: { count: 0, amount: 0 },
    delivered: { count: 0, amount: 0 },
    cancelled: { count: 0, amount: 0 },
   
    all: { count: 0, amount: 0 }
  };

  orders.forEach(order => {
    counts.all.count++;
    counts.all.amount += order.totalAmount;
    
    if (order.status === 'pending') {
      counts.pending.count++;
      counts.pending.amount += order.totalAmount;
    }
    if (order.status === 'accepted') {
      counts.accepted.count++;
      counts.accepted.amount += order.totalAmount;
    }
    if (order.status === 'delivered') {
      counts.delivered.count++;
      counts.delivered.amount += order.totalAmount;
    }
    if (order.status === 'cancelled') {
      counts.cancelled.count++;
      counts.cancelled.amount += order.totalAmount;
    }
  });

  return counts;
};

  const orderCounts = getOrderCounts();
 const formatAmount = (amount) => {
  const formatted = amount.toFixed(2);
  return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
};

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/api/user-orders`);
      setOrders(res.data);
      setFilteredOrders(res.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${config.apiBaseUrl}/api/user-orders/${id}`, { status: newStatus });
      fetchOrders();
    } catch (error) {
      console.error("Failed to update order status:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await axios.delete(`${config.apiBaseUrl}/api/user-orders/${id}`);
      fetchOrders();
    } catch (error) {
      console.error("Failed to delete order:", error);
    }
  };

  const toggleRowExpand = (id) => {
    if (expandedRows.includes(id)) {
      setExpandedRows(expandedRows.filter(rowId => rowId !== id));
    } else {
      setExpandedRows([...expandedRows, id]);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    let result = orders;
    
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(order => {
        if (searchField === "phone") {
          return order.shippingDetails?.phone?.toLowerCase().includes(lowerSearchTerm);
        } else if (searchField === "address") {
          return order.shippingDetails?.address?.toLowerCase().includes(lowerSearchTerm);
        } else if (searchField === "email") {
          return order.shippingDetails?.email?.toLowerCase().includes(lowerSearchTerm);
        }
        return true;
      });
    }
    
    if (statusFilter !== "all") {
      result = result.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(result);
  }, [searchTerm, statusFilter, orders, searchField]);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return styles.statusDefault;
      case "accepted":
        return styles.statusAccepted;
      case "delivered":
        return styles.statusDelivered;
      case "cancelled":
        return styles.statusCancelled;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Order Management</h2>
        <button className={styles.refreshButton} onClick={fetchOrders}>
          <FiRefreshCw className={styles.refreshIcon} /> Refresh
        </button>
      </div>
      <div className={styles.statusGrid}>
  <div className={`${styles.statusBox} ${styles.pending}`}>
    <span className={styles.statusLabel}>Pending</span>
    <span className={styles.statusCount}>{orderCounts.pending.count}</span>
    <span className={styles.statusAmount}>
      Total Amount: {formatAmount(orderCounts.pending.amount)} NPR
    </span>
  </div>
  <div className={`${styles.statusBox} ${styles.accepted}`}>
    <span className={styles.statusLabel}>Paid</span>
    <span className={styles.statusCount}>{orderCounts.accepted.count}</span>
    <span className={styles.statusAmount}>
      Total Amount: {formatAmount(orderCounts.accepted.amount)} NPR
    </span>
  </div>
  <div className={`${styles.statusBox} ${styles.delivered}`}>
    <span className={styles.statusLabel}>Delivered</span>
    <span className={styles.statusCount}>{orderCounts.delivered.count}</span>
    <span className={styles.statusAmount}>
      Total Amount: {formatAmount(orderCounts.delivered.amount)} NPR
    </span>
  </div>
  <div className={`${styles.statusBox} ${styles.cancelled}`}>
    <span className={styles.statusLabel}>Cancelled</span>
    <span className={styles.statusCount}>{orderCounts.cancelled.count}</span>
    <span className={styles.statusAmount}>
      Total Amount: {formatAmount(orderCounts.cancelled.amount)} NPR
    </span>
  </div>
  {/* Add the new Totals block */}
  <div className={`${styles.statusBox} ${styles.allOrders}`}>
    <span className={styles.statusLabel}>All Orders</span>
    <span className={styles.statusCount}>{orderCounts.all.count}</span>
    <span className={styles.statusAmount}>
      Total Amount: {formatAmount(orderCounts.all.amount)} NPR
    </span>
  </div>
</div>
      
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <div className={styles.searchIcon}>
            <FiSearch />
          </div>
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className={styles.fieldSelect}
          >
            <option value="phone">Phone Number</option>
            <option value="address">Shipping Address</option>
            <option value="email">Email</option>
          </select>
          <input
            type="text"
            placeholder={`Search by ${searchField === 'phone' ? 'phone number' : searchField === 'address' ? 'shipping address' : 'email'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterContainer}>
          <label htmlFor="status-filter" className={styles.filterLabel}>Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="accepted">Paid</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      
      {isLoading ? (
        <div className={styles.loaderContainer}>
          <div className={styles.loader}>Loading orders...</div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Phone</th>
                <th>Products</th>
                <th>Total</th>
                <th>Shipping Address</th>
                <th>Status</th>
                <th>Ordered at</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <React.Fragment key={order._id}>
                  <tr className={styles.tableRow}>
                    <td data-label="Email" className={styles.emailCell}>
                      {order.shippingDetails?.email || "N/A"}
                    </td>
                    <td data-label="Phone" className={styles.phoneCell}>
                      {order.shippingDetails?.phone || "N/A"}
                    </td>
                    <td data-label="Products" className={styles.productsCell}>
                      <div className={styles.productsList}>
                        {order.products.slice(0, 2).map((p, idx) => (
                          <div key={idx} className={styles.productItem}>
                            <span className={styles.productName}>{p.name}</span>
                            <span className={styles.productDetails}>
                              x{p.quantity} ({p.weight}kg)
                            </span>
                          </div>
                        ))}
                        {order.products.length > 2 && (
                          <div className={styles.moreItems}>
                            +{order.products.length - 2} more
                          </div>
                        )}
                      </div>
                    </td>
                    <td data-label="Total" className={styles.totalCell}>
                      Rs. {order.totalAmount.toFixed(2)}
                    </td>
                    <td data-label="Shipping Address" className={styles.shippingCell}>
                      {order.shippingDetails?.address ? (
                        <div className={styles.shippingAddress}>
                          {order.shippingDetails.address.substring(0, 30)}
                          {order.shippingDetails.address.length > 30 && "..."}
                        </div>
                      ) : "N/A"}
                    </td>
                    <td data-label="Status" className={styles.statusCell}>
                      <select
                        className={`${styles.statusSelect} ${getStatusColor(order.status)}`}
                        value={order.status}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="accepted">Paid</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td data-label="Created At" className={styles.dateCell}>
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td data-label="Actions" className={styles.actionsCell}>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDelete(order._id)}
                          title="Delete order"
                        >
                          <FiTrash2 />
                        </button>
                        <button
                          className={styles.expandButton}
                          onClick={() => toggleRowExpand(order._id)}
                          title={expandedRows.includes(order._id) ? "Collapse details" : "Expand details"}
                        >
                          {expandedRows.includes(order._id) ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      </div>
                    </td>
                     </tr>
                       {expandedRows.includes(order._id) && (
                    <tr className={styles.expandedRow}>
                    <td colSpan="8">
                  <div className={styles.expandedContent}>
                  <div className={styles.expandedSection}>
          <h4>Complete Shipping Details</h4>
          <p><strong>Address:</strong> {order.shippingDetails?.address || "N/A"}</p>
         
          <p><strong>Postal Code:</strong> {order.shippingDetails?.postalCode || "N/A"}</p>
        </div>
        <div className={styles.expandedSection}>
          <h4>All Products</h4>
          {order.products?.map((p, idx) => (
            <div key={idx} className={styles.expandedProduct}>
              <p><strong>{p.name || "Unnamed Product"}</strong></p>
              <p>Quantity: {p.quantity || 0}</p>
              <p>Weight: {p.weight || 0}kg</p>
              <p>Price: Rs. {(p.price || 0).toFixed(2)}</p>
            </div>
          ))}
        </div>
        <div className={styles.expandedSection}>
          <h4>Order Summary</h4>
          <p><strong>Shipping Fee:</strong> Rs. {(order.shippingCost|| 0).toFixed(2)}</p>
          <p><strong>Total Amount:</strong> Rs. {(order.totalAmount || 0).toFixed(2)}</p>
        </div>
      </div>
    </td>
  </tr>
)}
                </React.Fragment>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="8" className={styles.noData}>
                    No orders found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderedTable;